/**
 * JmPayDashboardPresenter
 * --
 */
import React from 'react';
import { Content, Title, Row, Col, ExpandableContainer } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
  Rectangle,
} from 'recharts';
import { stringToMoneyFormat } from 'utils';

/**----------------------------------------
 * [Component] 총관리자: 주문대시보드 - 프레젠터
 * ----------------------------------------
 */
const JmPayDashboardPresenter = ({
  loading,
  chartData,
  statisticsType,
  setStatisticsType,
  handleGetCartList,

  //
  userData,
  orderData,
  orderData_date,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  // console.log('[JmPayDashboardPresenter] userData: ', userData);
  // console.log('[JmPayDashboardPresenter] orderData: ', orderData);
  // console.log('[JmPayDashboardPresenter] orderData_date: ', orderData_date);

  const totalUserCount = userData.length;
  const totalAmount = userData.reduce((acc, cur) => acc + cur.jm_pay, 0);
  const spentData = orderData.filter((i) => i.type === 'USE');
  const totalAmountSpent = spentData.reduce(
    (acc, cur) => acc + Math.abs(cur.price),
    0
  );
  const totalChargedData = orderData.filter(
    (i) => i.type === 'AUTO_CHARGE' || i.type === 'MANUAL_CHARGE'
  );
  const totalChargedAmount = totalChargedData.reduce(
    (acc, cur) => acc + Math.abs(cur.price),
    0
  );

  // 차트데이터
  /* 
  [
    {
      name: '2024.10',
      "충전금액": 2000, //
      "사용금액": 9800,
      amt: 2290,
    },
  ]
  */
  const keys = Object.keys(orderData_date);
  const chartData2 = keys.map((key) => {
    const used = orderData_date[key].filter((i) => i.type === 'USE');
    const charged = orderData_date[key].filter(
      (i) => i.type === 'AUTO_CHARGE' || i.type === 'MANUAL_CHARGE'
    );

    return {
      name: key,
      ['사용건수']: used.length,
      ['사용금액']: used.reduce((acc, cur) => acc + Math.abs(cur.price), 0),
      ['충전건수']: charged.length,
      ['충전금액']: charged.reduce((acc, cur) => acc + cur.price, 0),
    };
  });
  // console.log('chartData2: ', chartData2);

  // JSX: start
  return (
    <Spin spinning={loading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title size={3} style={{ marginBottom: 12 }}>
              주문 현황
            </Title>

            {/* === 현황 요약: start === */}
            <Content
              fullWidth
              padding={0}
              backgroundColor={'none'}
              style={{
                marginBottom: 5,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {[
                {
                  title: '이용자 수',
                  value: stringToMoneyFormat(totalUserCount),
                  suffix: '명',
                },
                {
                  title: '총 잔액',
                  value: stringToMoneyFormat(totalAmount),
                  suffix: '원',
                },
                {
                  title: '결제금액',
                  value: stringToMoneyFormat(totalAmountSpent),
                  suffix: '원',
                },
                {
                  title: '결제건수',
                  value: stringToMoneyFormat(spentData.length),
                  suffix: '건',
                },

                {
                  title: '누적 충전금액',
                  value: stringToMoneyFormat(totalChargedAmount),
                  suffix: '원',
                },
              ].map((_item, _index) => (
                <div
                  key={`${_item.label}-${_index}`}
                  className="boxShadow"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `90%`,
                    margin: '0 2px',
                    padding: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Statistic
                    className="cursorOverOpacity"
                    direction={'row'}
                    size="large"
                    title={_item.title}
                    value={_item.value}
                    suffix={_item.suffix}
                    // style={{ justifyContent: 'center' }}
                    valueStyle={{
                      color: '#1677ff',
                      textDecoration: 'underline !important',
                    }}
                  />
                  {/* <div>
                    <Button shape="circle">
                      <ExportOutlined />
                    </Button>
                  </div> */}
                </div>
              ))}
            </Content>
            {/* === 현황 요약: end === */}

            {/* ===== 차트: start ===== */}
            <Row gutter={[5, 5]}>
              {/* === 상품별: start === */}
              <Col x={11}>
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  // padding={'14px 5px'}
                  height={570}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    일별 매출현황
                  </Title>
                  <Divider style={{ margin: 0 }} />
                  <ResponsiveContainer
                    width="100%"
                    height={465}
                    style={{ marginTop: 25 }}
                  >
                    <BarChart
                      width={500}
                      // height={300}
                      data={chartData2 ?? []}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="사용금액"
                        fill="#8884d8"
                        activeBar={<Rectangle fill="pink" stroke="blue" />}
                      />
                      <Bar
                        dataKey="충전금액"
                        fill="#82ca9d"
                        activeBar={<Rectangle fill="gold" stroke="purple" />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Content>
              </Col>
              {/* === 상품별: end === */}

              {/* === 내역: start === */}
              <Col x={13}>
                <Content
                  maxWidth={'100%'}
                  height={570}
                  borderRadius={4}
                  // padding={'14px 5px'}
                >
                  <Title size={5} style={{ marginBottom: 12 }}>
                    일별 주문내역
                  </Title>

                  <Table
                    bordered
                    // size="small"
                    scroll={{ x: 650, y: 450 }}
                    pagination={false}
                    dataSource={chartData2 ?? []}
                    columns={[
                      {
                        title: '날짜',
                        dataIndex: 'name',
                        width: 130,
                        align: 'center',
                      },
                      {
                        title: '주문건수',
                        dataIndex: '사용건수',
                        width: 130,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}`,
                      },
                      {
                        title: '사용금액',
                        dataIndex: '사용금액',
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}`,
                      },
                      {
                        title: '충전건수',
                        dataIndex: '충전건수',
                        width: 130,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}`,
                      },
                      {
                        title: '충전금액',
                        dataIndex: '충전금액',
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}`,
                      },
                    ]}
                  />
                </Content>
              </Col>
              {/* === 내역: end === */}
            </Row>
            {/* ===== 차트: end ===== */}
          </Col>

          <Col x={24}>
            {/* <br /> */}
            <Button
              block
              size="large"
              style={{ marginTop: 5 }}
              onClick={() => window.open('/jm-pay/list', '_blank')}
            >
              JM페이 바로가기 <ExportOutlined />
            </Button>
          </Col>
        </Row>
      </Content>
    </Spin>
  ); // JSX: end
};

export default JmPayDashboardPresenter;
